"use client"

import React from "react"
import { CalendarIcon, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useFormatters } from "@/hooks/use-formatters"

const focusClasses = "focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring focus:ring-offset-2"
const disabledClasses = "cursor-not-allowed opacity-50"
const errorClasses = "data-[show-error]:aria-[invalid=true]:border-destructive data-[show-error]:aria-[invalid=true]:ring-destructive"

interface DatePickerProps {
  value?: Date | null
  onChange: (date: Date | null) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  minDate?: Date
  maxDate?: Date
  allowClear?: boolean
  /**
   * When true, the date can be typed directly into the field (parsed on blur/Enter) in addition
   * to picking from the calendar. Typed input that is unparseable or outside min/max snaps back to
   * the last valid value; clearing the text clears the field.
   */
  allowTextInput?: boolean
  id?: string
  "data-show-error"?: boolean
  "aria-invalid"?: boolean
  "aria-describedby"?: string
}

/**
 * Build a local-midnight Date from numeric components, or null if they don't form a real calendar
 * date. new Date(2026, 1, 31) silently rolls over to Mar 3 (and is not NaN), so we verify every
 * component survives the round-trip rather than trusting isNaN.
 */
function buildDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

/**
 * Parse free-typed text into a date-only value, or null if unparseable. Accepts ISO (yyyy-mm-dd),
 * locale numeric (mm/dd/yyyy), and long forms (e.g. "Jun 9, 2026"). Every branch rejects rolled-over
 * invalid input (e.g. 02/31, "Jun 31") rather than silently saving a different date.
 */
function parseTypedDate(text: string): Date | null {
  const trimmed = text.trim()
  if (!trimmed) return null

  // ISO yyyy-mm-dd — handled explicitly to avoid the UTC-midnight day-shift that
  // `new Date('2026-06-09')` introduces in negative-offset time zones.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed)
  if (iso) {
    return buildDate(Number(iso[1]), Number(iso[2]), Number(iso[3]))
  }

  // Locale numeric mm/dd/yyyy.
  const numeric = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed)
  if (numeric) {
    return buildDate(Number(numeric[3]), Number(numeric[1]), Number(numeric[2]))
  }

  // Long forms (e.g. "Jun 9, 2026"). new Date rolls an invalid day forward ("Jun 31, 2026" -> Jul 1)
  // without going NaN, so reject any result whose day-of-month doesn't match the typed day number.
  const parsed = new Date(trimmed)
  if (isNaN(parsed.getTime())) return null
  // Only apply the day-token rollover guard to genuine alphabetic long forms — numeric strings that
  // slip past the strict slash regex (e.g. a 2-digit-year "06/09/26") would otherwise have their
  // leading month digits mistaken for the day token and be wrongly rejected.
  if (/[a-z]/i.test(trimmed)) {
    const dayToken = /\b(\d{1,2})\b/.exec(trimmed)
    if (dayToken && Number(dayToken[1]) !== parsed.getDate()) return null
  }
  // Normalize to local midnight so the stored value carries no time component.
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

/**
 * Format a Date as a locale-independent yyyy-mm-dd string. Used as the editable representation so
 * typing round-trips regardless of the display locale (the pretty, localized form is only shown
 * when the field is not being edited).
 */
function toIsoDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(({
    value,
    onChange,
    onBlur,
    placeholder = "Pick a date",
    disabled,
    className,
    minDate,
    maxDate,
    allowClear = true,
    allowTextInput = false,
    id,
    "data-show-error": showError,
    "aria-invalid": invalid,
    "aria-describedby": describedBy,
  }, ref) => {
    const [open, setOpen] = React.useState(false)
    const [isEditing, setIsEditing] = React.useState(false)
    const [inputText, setInputText] = React.useState("")
    const { formatDate } = useFormatters()

    // Calculate which month to show when calendar opens
    const defaultMonth = React.useMemo(() => {
      // If there's a selected value, show that month
      if (value) return value

      // If no value, determine best month based on boundaries
      const today = new Date()

      // If today exceeds maxDate, show maxDate's month
      if (maxDate && today > maxDate) {
        return maxDate
      }

      // If today is before minDate, show minDate's month
      if (minDate && today < minDate) {
        return minDate
      }

      // Otherwise show current month (undefined = today)
      return undefined
    }, [value, minDate, maxDate])

    const isOutOfRange = React.useCallback((date: Date) => {
      if (minDate && date < minDate) return true
      if (maxDate && date > maxDate) return true
      return false
    }, [minDate, maxDate])

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
      setInputText("")
      setIsEditing(false)
      onBlur?.()
    }

    const handleMainClick = () => {
      if (!disabled) {
        setOpen(true)
      }
    }

    // Commit typed text: apply if valid & in range, clear if emptied, otherwise snap back to last valid value.
    const commitTypedValue = () => {
      const parsed = parseTypedDate(inputText)
      if (inputText.trim() === "") {
        // Only treat an emptied field as a clear when clearing is allowed. For a non-clearable
        // (required) field, leave the existing value untouched so the display snaps back below.
        if (allowClear) onChange(null)
      } else if (parsed && !isOutOfRange(parsed)) {
        onChange(parsed)
      }
      // else: unparseable or out of range -> leave the existing value untouched; display snaps back below.
      setIsEditing(false)
      onBlur?.()
    }

    // While editing, show the locale-independent ISO form so typing round-trips in any locale;
    // otherwise show the pretty, localized format.
    const displayValue = isEditing ? inputText : (value ? formatDate(value) : "")

    // Bordered clear segment shared by both render branches.
    const clearButton = allowClear && value && !disabled ? (
      <button
        type="button"
        onClick={handleClear}
        className={cn(
          "flex items-center justify-center h-10 border border-input border-l-0 px-2 py-2 rounded-r-md hover:bg-muted ring-offset-background focus-visible:border-l focus-visible:border-l-ring",
          focusClasses
        )}
      >
        <X className="h-3 w-3" />
        <span className="sr-only">Clear date</span>
      </button>
    ) : null

    const calendar = (
      <Calendar
        mode="single"
        selected={value || undefined}
        onSelect={(date) => {
          onChange(date || null)
          setIsEditing(false)
          setOpen(false)
          onBlur?.()
        }}
        defaultMonth={defaultMonth}
        fixedWeeks
        disabled={[
          ...(disabled ? [() => true] : []),
          ...(minDate ? [{ before: minDate }] : []),
          ...(maxDate ? [{ after: maxDate }] : [])
        ]}
        initialFocus
      />
    )

    if (allowTextInput) {
      return (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <div className={cn("flex w-full bg-background rounded-md")}>
              <div className="relative flex-1">
                {/* Calendar toggle - opens the date picker popover */}
                <PopoverTrigger asChild>
                  <button
                    ref={ref}
                    type="button"
                    tabIndex={-1}
                    disabled={disabled}
                    aria-label="Open calendar"
                    onClick={handleMainClick}
                    className={cn(
                      "absolute inset-y-0 left-0 flex items-center justify-center px-3 text-muted-foreground",
                      disabled && disabledClasses
                    )}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <input
                  id={id}
                  type="text"
                  value={displayValue}
                  placeholder={placeholder}
                  disabled={disabled}
                  data-show-error={showError}
                  aria-invalid={invalid}
                  aria-describedby={describedBy}
                  onFocus={() => {
                    setIsEditing(true)
                    setInputText(value ? toIsoDateString(value) : "")
                  }}
                  onChange={(e) => {
                    setIsEditing(true)
                    setInputText(e.target.value)
                  }}
                  onBlur={commitTypedValue}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      commitTypedValue()
                      ;(e.target as HTMLInputElement).blur()
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault()
                      setOpen(true)
                    }
                  }}
                  className={cn(
                    "h-10 w-full border border-input bg-background py-2 pl-9 pr-3 text-sm ring-offset-background placeholder:text-muted-foreground",
                    focusClasses,
                    errorClasses,
                    allowClear && value ? "rounded-l-md" : "rounded-md",
                    disabled && disabledClasses,
                    className
                  )}
                />
              </div>

              {/* Clear button area - separate clickable zone */}
              {clearButton}
            </div>
          </PopoverAnchor>

          <PopoverContent
            className="w-auto p-0"
            onOpenAutoFocus={(e) => {
              // Keep focus in the text input rather than stealing it into the calendar grid.
              e.preventDefault()
            }}
          >
            {calendar}
          </PopoverContent>
        </Popover>
      )
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div className={cn(
          "flex w-full bg-background rounded-md"
        )}>
          {/* Main clickable area - opens calendar */}
          <PopoverTrigger asChild>
            <button
              ref={ref}
              id={id}
              type="button"
              disabled={disabled}
              onClick={handleMainClick}
              data-show-error={showError}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className={cn(
                "flex-1 flex items-center justify-start text-left font-normal h-10 border border-input px-3 py-2 text-sm ring-offset-background",
                focusClasses,
                errorClasses,
                allowClear && value ? "rounded-l-md" : "rounded-md",
                !value && "text-muted-foreground",
                disabled && disabledClasses,
                className
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? formatDate(value) : <span>{placeholder}</span>}
            </button>
          </PopoverTrigger>

          {/* Clear button area - separate clickable zone */}
          {clearButton}
        </div>

        <PopoverContent
          className="w-auto p-0"
          onCloseAutoFocus={(e) => {
            onBlur?.()
          }}
        >
          {calendar}
        </PopoverContent>
      </Popover>
    )
  })

DatePicker.displayName = "DatePicker"
